import { Injectable, inject, signal } from '@angular/core';
import { ApiService } from '../../core/services/api.service';
import { Observable, tap } from 'rxjs';
import { SystemModule, SidebarItem, ModuleQueryOptions, ModulePageResult } from '../models';

// ─── Grupos del sidebar ───────────────────────────────────────────────────────

export const SIDEBAR_GROUPS: { key: string; label: string; icon: string }[] = [
    { key: 'clinic', label: 'Clínica', icon: 'solar:health-bold-duotone' },
    { key: 'finance', label: 'Finanzas', icon: 'solar:wallet-bold-duotone' },
    { key: 'admin', label: 'Administración', icon: 'solar:settings-bold-duotone' },
    { key: 'system', label: 'Sistema', icon: 'solar:server-bold-duotone' },
];

// ─── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class ModuleService {
    private api = inject(ApiService);

    private _allModules = signal<SystemModule[]>([]);
    readonly allModules = this._allModules.asReadonly();

    // ── Lectura ──────────────────────────────────────────────────────────────

    /**
     * Carga módulos con paginación y búsqueda avanzada standard.
     *
     * @example
     * loadAll()                                 // solo activos (sidebar)
     * loadAll({ all: true, page: 1, limit: 20 }) // admin con paginación
     * loadAll({ search: 'clinic' })             // búsqueda OR sobre texto
     */
    loadAll(opts: ModuleQueryOptions = {}): Observable<ModulePageResult> {
        const params = new URLSearchParams();
        if (opts.all) params.set('all', 'true');
        if (opts.page) params.set('page', String(opts.page));
        if (opts.limit) params.set('limit', String(opts.limit));
        if (opts.search) params.set('search', opts.search);
        if (opts.group) params.set('group', opts.group);
        if (opts.isActive !== undefined) params.set('isActive', String(opts.isActive));

        const qs = params.toString();
        const url = qs ? `system-modules?${qs}` : 'system-modules';

        return this.api.get<ModulePageResult>(url);
    }

    /**
     * Carga inicial de módulos para el menú lateral (Sidebar).
     * Solo carga activos (all=false) y actualiza el estado global.
     */
    initSidebarModules(): Observable<ModulePageResult> {
        return this.loadAll({ all: false, limit: 100 }).pipe(
            tap(res => this._allModules.set(res?.data ?? []))
        );
    }

    /** Obtiene un módulo por ID */
    getById(id: string): Observable<SystemModule> {
        return this.api.get<SystemModule>(`system-modules/${id}`);
    }

    /** Módulos accesibles según permisos (para sidebar) */
    loadForPermissions(permissions: string[]): Observable<{ data: SystemModule[] }> {
        const permsParam = permissions.join(',');
        return this.api.get<{ data: SystemModule[] }>(
            `system-modules/by-permissions?perms=${encodeURIComponent(permsParam)}`
        );
    }

    /** Obtiene metadata (grupos y acciones comunes) */
    getMetadata(): Observable<{ groups: any[], actions: any[] }> {
        return this.api.get<{ groups: any[], actions: any[] }>('system-modules/metadata');
    }

    /** Filtra módulos localmente por permisos (sin API call) */
    filterByPermissions(permissions: string[]): SystemModule[] {
        if (permissions.includes('*')) return this._allModules();
        const moduleKeys = new Set(permissions.map(p => p.split(':')[0]));
        return this._allModules().filter(m => moduleKeys.has(m.key));
    }

    // ── Sidebar builder ──────────────────────────────────────────────────────

    buildSidebarGroups(permissions: string[]): {
        group: string; label: string; icon: string; items: SidebarItem[];
    }[] {
        const accessible = this.filterByPermissions(permissions);

        return SIDEBAR_GROUPS
            .map(g => ({
                group: g.key,
                label: g.label,
                icon: g.icon,
                items: accessible
                    .filter(m => m.group === g.key && m.isActive)
                    .sort((a, b) => a.order - b.order)
                    .map(m => ({
                        key: m.key,
                        label: m.label,
                        icon: m.icon,
                        route: m.route,
                        group: m.group,
                        order: m.order,
                    })),
            }))
            .filter(g => g.items.length > 0);
    }

    // ── CRUD (Solo SuperAdmin) ────────────────────────────────────────────────

    create(data: Partial<SystemModule>): Observable<SystemModule> {
        return this.api.post<SystemModule>('system-modules', data);
    }

    update(id: string, data: Partial<SystemModule>): Observable<SystemModule> {
        return this.api.put<SystemModule>(`system-modules/${id}`, data);
    }

    delete(id: string): Observable<{ deleted: boolean }> {
        return this.api.delete<{ deleted: boolean }>(`system-modules/${id}`);
    }
}
